import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { ADMIN_KIND_GROUPS, KIND_GROUPS } from "@strat/notify";
import type { NotificationKind, NotificationPrefs } from "@strat/notify";

import { api } from "../api";
import { currentSubscription, disablePush, enablePush, pushSupport } from "../push";
import { useSession } from "../session";
import { Alert, Card } from "../shell/ui";

const LABELS: Record<NotificationKind, { label: string; hint: string }> = {
  trade_closed: { label: "A trade closes", hint: "Stop, target or signal exit" },
  liquidation: { label: "A position is liquidated", hint: "Always worth knowing, even on paper" },
  bot_crashed: { label: "A bot crashes", hint: "It restarts by itself; one message per crash loop" },
  bot_stopped: { label: "A bot stops", hint: "When it is no longer running" },
  feed_gap: { label: "A bot loses its market feed", hint: "It missed bars, and its record shows the gap" },
  position_opened: { label: "A position opens", hint: "Can be frequent on short timeframes" },
  bot_drawdown: { label: "A bot's drawdown passes my level", hint: "Set the level below" },
  bot_profit: { label: "A bot's return passes my level", hint: "Set the level below" },
  price_alert: { label: "A price alert fires", hint: "The alerts you set under Price alerts" },
  daily_summary: { label: "A daily summary", hint: "One message a day: trades closed and bots running" },
  social: {
    label: "Someone rates, comments on, forks or follows my things",
    hint: "Not everyone who follows you, only what is about you",
  },
  followed_bot: { label: "A bot I follow closes a trade", hint: "Can be frequent" },
  ops_alert: {
    label: "Something on the platform needs looking at",
    hint: "A service stops, a bot crashes or stalls, market data falls behind. Admins only",
  },
};

/**
 * One switch with its explanation. The label and hint are one wrapping span, so a
 * long line breaks inside the card instead of running out of it (and, in a nowrap
 * flex row, squeezing the checkbox itself to nothing, which is what used to happen).
 */
function Check({
  checked,
  onChange,
  label,
  hint,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  hint?: string;
}) {
  return (
    <label className="checkbox checkbox--wrap">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span>
        {label}
        {hint && <span className="muted small"> · {hint}</span>}
      </span>
    </label>
  );
}

const HOURS = Array.from({ length: 24 }, (_, h) => h);
const hourLabel = (h: number) => `${String(h).padStart(2, "0")}:00`;

function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

function timezones(): string[] {
  try {
    return (Intl as unknown as { supportedValuesOf: (k: string) => string[] }).supportedValuesOf("timeZone");
  } catch {
    return ["UTC", "Europe/Brussels", "Europe/London", "America/New_York", "Asia/Tokyo"];
  }
}

interface DeviceRow {
  id: string;
  endpoint: string;
  service: string;
  deviceLabel: string;
}

/**
 * What you hear about, where, and when: turn push on for this browser, choose the
 * messages, set the levels bot alerts use, and set quiet hours. Price alerts have their
 * own card. Everything you are sent also lands in the Alerts inbox.
 */
export function NotificationsCard() {
  const qc = useQueryClient();
  const support = pushSupport();
  const [error, setError] = useState<string | null>(null);
  const [tested, setTested] = useState(false);
  const admin = useSession().layout === "admin";

  const key = useQuery({ queryKey: ["push", "key"], queryFn: () => api<{ enabled: boolean }>("/push/key") });
  const here = useQuery({
    queryKey: ["push", "here"],
    queryFn: async () => (await currentSubscription())?.endpoint ?? null,
  });
  const devices = useQuery({
    queryKey: ["push", "devices"],
    queryFn: () => api<{ items: DeviceRow[] }>("/push/subscriptions"),
  });
  const prefsQuery = useQuery({
    queryKey: ["push", "prefs"],
    queryFn: () => api<{ prefs: NotificationPrefs }>("/push/prefs"),
  });
  const space = useQuery({
    queryKey: ["me-space-bots"],
    queryFn: () =>
      api<{ bots: Array<{ id: string; name: string; symbol: string; timeframe: string }> }>("/hub/me/space"),
  });

  // The levels are typed, so they are edited locally and saved when the field is left.
  const [drawdown, setDrawdown] = useState("");
  const [profit, setProfit] = useState("");
  const p = prefsQuery.data?.prefs;
  useEffect(() => {
    if (p) {
      setDrawdown(String(p.drawdownPct));
      setProfit(String(p.profitPct));
    }
  }, [p]);

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["push"] });
  };
  const enable = useMutation({
    mutationFn: enablePush,
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: (e: Error) => setError(e.message),
  });
  const disable = useMutation({
    mutationFn: disablePush,
    onSuccess: refresh,
    onError: (e: Error) => setError(e.message),
  });
  const savePrefs = useMutation({
    mutationFn: (next: NotificationPrefs) => api("/push/prefs", { method: "PUT", body: { prefs: next } }),
    onSuccess: refresh,
  });
  const removeDevice = useMutation({
    mutationFn: (endpoint: string) => api("/push/subscribe", { method: "DELETE", body: { endpoint } }),
    onSuccess: refresh,
  });
  const test = useMutation({
    mutationFn: () => api("/push/test", { method: "POST", body: {} }),
    onSuccess: () => {
      setError(null);
      setTested(true);
    },
    onError: (e: Error) => setError(e.message),
  });

  if (key.data && !key.data.enabled) {
    return (
      <Card title="Notifications">
        <p className="muted">Push notifications are not switched on for this server.</p>
      </Card>
    );
  }

  const subscribedHere = !!here.data && !!devices.data?.items.some((d) => d.endpoint === here.data);
  const save = (patch: Partial<NotificationPrefs>) => {
    if (p) savePrefs.mutate({ ...p, ...patch });
  };
  const toggle = (kind: NotificationKind, on: boolean) => {
    if (p) save({ events: { ...p.events, [kind]: on } });
  };
  const zones = timezones();
  const deviceZone = browserTimezone();

  return (
    <Card title="Notifications">
      <div className="stack">
        {support === "ios-needs-install" && (
          <Alert tone="error">
            On an iPhone or iPad, notifications only work once StrategyHive is on your Home Screen: tap Share,
            then Add to Home Screen, and open it from there.
          </Alert>
        )}
        {support === "unsupported" && (
          <p className="muted">
            This browser can't receive push notifications here. They need a modern browser on a secure (https)
            connection. Everything is still kept in your Alerts inbox.
          </p>
        )}
        {support === "denied" && (
          <Alert tone="error">
            Notifications are blocked for this site. Allow them in your browser's site settings, then reload
            this page.
          </Alert>
        )}

        {support === "ready" && (
          <div className="row">
            {subscribedHere ? (
              <>
                <span className="badge">On for this browser</span>
                <button
                  type="button"
                  className="btn"
                  onClick={() => disable.mutate()}
                  disabled={disable.isPending}
                >
                  Turn off here
                </button>
                <button type="button" className="btn" onClick={() => test.mutate()} disabled={test.isPending}>
                  Send a test
                </button>
              </>
            ) : (
              <button
                type="button"
                className="btn btn--primary"
                onClick={() => enable.mutate()}
                disabled={enable.isPending}
              >
                Turn on for this browser
              </button>
            )}
          </div>
        )}
        {tested && <Alert tone="ok">A test was sent. It should arrive in a few seconds.</Alert>}
        {error && <Alert tone="error">{error}</Alert>}

        {p && (
          <>
            <Check
              checked={p.enabled}
              onChange={(v) => save({ enabled: v })}
              label="Notifications are on"
              hint="the master switch, for every device and the inbox"
            />

            {KIND_GROUPS.filter((g) => admin || !ADMIN_KIND_GROUPS.includes(g.id)).map((g) => (
              <fieldset key={g.id} className="stack" disabled={!p.enabled}>
                <legend className="muted small">{g.label}</legend>
                {g.kinds.map((kind) => (
                  <Check
                    key={kind}
                    checked={p.events[kind]}
                    onChange={(v) => toggle(kind, v)}
                    label={LABELS[kind].label}
                    hint={LABELS[kind].hint}
                  />
                ))}
                {g.id === "bots" && (
                  <div className="row">
                    <label className="field">
                      <span className="field__label">Drawdown level (%)</span>
                      <input
                        type="number"
                        min={0}
                        max={95}
                        step={1}
                        value={drawdown}
                        onChange={(e) => setDrawdown(e.target.value)}
                        onBlur={() => save({ drawdownPct: Number(drawdown) || 0 })}
                      />
                    </label>
                    <label className="field">
                      <span className="field__label">Return level (%)</span>
                      <input
                        type="number"
                        min={0}
                        max={10000}
                        step={1}
                        value={profit}
                        onChange={(e) => setProfit(e.target.value)}
                        onBlur={() => save({ profitPct: Number(profit) || 0 })}
                      />
                    </label>
                    <span className="muted small">0 switches a level off. Each fires once per bot.</span>
                  </div>
                )}
                {g.id === "alerts" && p.events.daily_summary && (
                  <label className="field">
                    <span className="field__label">Send the summary at</span>
                    <select
                      value={p.summaryHour}
                      onChange={(e) => save({ summaryHour: Number(e.target.value) })}
                    >
                      {HOURS.map((h) => (
                        <option key={h} value={h}>
                          {hourLabel(h)}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
              </fieldset>
            ))}

            <fieldset className="stack" disabled={!p.enabled}>
              <legend className="muted small">Quiet hours</legend>
              <Check
                checked={p.quiet.enabled}
                onChange={(v) => save({ quiet: { ...p.quiet, enabled: v } })}
                label="No pushes during these hours"
                hint="they still land in your Alerts inbox"
              />
              {p.quiet.enabled && (
                <>
                  <div className="row">
                    <label className="field">
                      <span className="field__label">From</span>
                      <select
                        value={p.quiet.start}
                        onChange={(e) => save({ quiet: { ...p.quiet, start: Number(e.target.value) } })}
                      >
                        {HOURS.map((h) => (
                          <option key={h} value={h}>
                            {hourLabel(h)}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="field">
                      <span className="field__label">Until</span>
                      <select
                        value={p.quiet.end}
                        onChange={(e) => save({ quiet: { ...p.quiet, end: Number(e.target.value) } })}
                      >
                        {HOURS.map((h) => (
                          <option key={h} value={h}>
                            {hourLabel(h)}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                  <Check
                    checked={p.quiet.allowUrgent}
                    onChange={(v) => save({ quiet: { ...p.quiet, allowUrgent: v } })}
                    label="Still tell me about liquidations and crashes"
                  />
                </>
              )}
              <label className="field">
                <span className="field__label">My time zone (quiet hours and the summary use it)</span>
                <input
                  list="timezones"
                  defaultValue={p.timezone}
                  key={p.timezone}
                  onBlur={(e) => {
                    const v = e.target.value.trim();
                    if (v && v !== p.timezone && zones.includes(v)) save({ timezone: v });
                    else e.target.value = p.timezone;
                  }}
                />
                <datalist id="timezones">
                  {zones.map((z) => (
                    <option key={z} value={z} />
                  ))}
                </datalist>
              </label>
              {p.timezone !== deviceZone && (
                <button type="button" className="btn btn--sm" onClick={() => save({ timezone: deviceZone })}>
                  Use this device's time zone ({deviceZone})
                </button>
              )}
            </fieldset>

            <Check
              checked={p.includePnl}
              onChange={(v) => save({ includePnl: v })}
              label="Show profit and loss in the notification"
              hint="visible on a locked screen"
            />

            {(space.data?.bots.length ?? 0) > 0 && (
              <details>
                <summary className="muted small">
                  Silence individual bots ({p.mutedBots.length} silenced)
                </summary>
                <ul className="stack">
                  {space.data!.bots.map((b) => (
                    <li key={b.id}>
                      <Check
                        checked={p.mutedBots.includes(b.id)}
                        onChange={(v) =>
                          save({
                            mutedBots: v ? [...p.mutedBots, b.id] : p.mutedBots.filter((x) => x !== b.id),
                          })
                        }
                        label={b.name}
                        hint={`${b.symbol} ${b.timeframe}`}
                      />
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </>
        )}

        {devices.data && devices.data.items.length > 0 && (
          <div>
            <h3 className="small muted">Your devices</h3>
            <ul className="stack">
              {devices.data.items.map((d) => (
                <li key={d.id} className="row">
                  <span>
                    {d.deviceLabel || d.service}
                    {d.endpoint === here.data && <span className="muted small"> · this browser</span>}
                  </span>
                  <button
                    type="button"
                    className="btn btn--sm"
                    onClick={() => removeDevice.mutate(d.endpoint)}
                    disabled={removeDevice.isPending}
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Card>
  );
}
